import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';

import { noop, getUrl } from '../lib/serviceHelpers';
import { useUser } from '../contexts/UserContext';

export const useFetchUsers = (
  page,
  search,
  sortBy,
  sortDir,
  userType = undefined,
) => {
  const cacheKey = userType || 'users';

  return useQuery(
    [cacheKey, { page, search, sortBy, sortDir, userType }],
    async () => {
      const url = getUrl('/users', {
        page: page || 1,
        search,
        sortBy,
        sortDir,
        userType,
      });
      const res = await axios.get(url);
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5000,
    },
  );
};

export const useFetchUserExpanded = (userId) => {
  const cacheKey = `user-${userId}`;

  return useQuery(
    cacheKey,
    async () => {
      const url = getUrl(`/users/${userId}`, {
        expanded: true,
      });
      const res = await axios.get(url);
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5000,
    },
  );
};

export const useCreateUser = (callback, opts = {}) => {
  return useMutation(
    async (payload) => {
      const res = await axios.post(`/users`, {
        ...payload,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useUpdateUser = (callback, opts = {}) => {
  return useMutation(
    async ({ userId, payload }) => {
      const res = await axios.put(`/users/${userId}`, {
        ...payload,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useDeleteUser = (callback, opts = {}) => {
  return useMutation(
    async (userId) => {
      const res = await axios.delete(`/users/${userId}`);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useFetchAdmins = (page, search, sortBy, sortDir, enabled = true) => {
  return useQuery(
    ['admins', { page, search, sortBy, sortDir }],
    async () => {
      const url = getUrl('/users', {
        userType: 'internal',
        page: page || 1,
        search,
        sortBy,
        sortDir,
      });
      const res = await axios.get(url);
      return res.data;
    },
    {
      enabled,
      refetchOnWindowFocus: false,
      staleTime: 5000,
    },
  );
};

export const useCreateAdmin = (callback, opts = {}) => {
  return useMutation(
    async (payload) => {
      const res = await axios.post(`/users/admin`, {
        ...payload,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useUpdateAdmin = (callback, opts = {}) => {
  return useMutation(
    async ({ userId, payload }) => {
      const res = await axios.put(`/users/admin/${userId}`, {
        ...payload,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

// Claims Managers In Training (Nic 2026-10-30): the Claims Managers a
// Manage Trainees holder sees on Internal Users, and the one mark.
export const useFetchClaimsManagers = (enabled = true) => {
  return useQuery(
    ['claims-managers'],
    async () => {
      const res = await axios.get('/users/admin/claims-managers');
      return res.data;
    },
    { enabled, refetchOnWindowFocus: false, staleTime: 5000 },
  );
};

export const useSetCmTraining = (callback, opts = {}) => {
  return useMutation(
    async ({ userId, inTraining }) => {
      const res = await axios.put(`/users/admin/${userId}/training`, { inTraining });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useSyncRcNumbers = (callback, opts = {}) => {
  return useMutation(
    async () => {
      const res = await axios.post(`/users/admin/sync-rc-numbers`);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useCreateProvider = (callback, opts = {}) => {
  return useMutation(
    async (payload) => {
      const res = await axios.post(`/users/provider`, {
        ...payload,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useUpdateProvider = (callback, opts = {}) => {
  return useMutation(
    async ({ userId, payload }) => {
      const res = await axios.put(`/users/provider/${userId}`, {
        ...payload,
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useUploadProviderSignature = (callback, opts = {}) => {
  return useMutation(
    async ({ userId, file }) => {
      const formData = new FormData();
      formData.append('signature', file);
      const res = await axios.post(`/users/provider/${userId}/signature`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useUploadProviderLetterhead = (callback, opts = {}) => {
  return useMutation(
    async ({ userId, file }) => {
      const formData = new FormData();
      formData.append('letterhead', file);
      const res = await axios.post(`/users/provider/${userId}/letterhead`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useFetchProviderProfile = (userId) => {
  return useQuery(
    ['provider-profile', userId],
    async () => {
      const res = await axios.get(`/users/provider/${userId}/profile`);
      return res.data;
    },
    {
      enabled: !!userId,
      refetchOnWindowFocus: false,
      staleTime: 5000,
    },
  );
};

// ============================================================================
// PROVIDER CERTIFICATION PROFILE HOOKS (Admin access)
// ============================================================================

export const useCreateProviderProfile = (callback, opts = {}) => {
  return useMutation(
    async ({ userId, payload }) => {
      const res = await axios.post(`/users/provider/${userId}/profiles`, payload);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useUpdateProviderProfile = (callback, opts = {}) => {
  return useMutation(
    async ({ userId, profileId, payload }) => {
      const res = await axios.put(`/users/provider/${userId}/profiles/${profileId}`, payload);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useDeleteProviderProfile = (callback, opts = {}) => {
  return useMutation(
    async ({ userId, profileId }) => {
      const res = await axios.delete(`/users/provider/${userId}/profiles/${profileId}`);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useSetDefaultProviderProfile = (callback, opts = {}) => {
  return useMutation(
    async ({ userId, profileId }) => {
      const res = await axios.put(`/users/provider/${userId}/profiles/${profileId}/default`);
      return res.data;
    },
    { ...opts, onSuccess: callback || noop },
  );
};

export const useExportUsers = (callback, opts = {}) => {
  const { token } = useUser();

  return useMutation(
    async () => {
      try {
        const res = await axios.get('/users/export', {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        return res.data;
      } catch (error) {
        console.error('Error exporting users:', error);
        throw error;
      }
    },
    { ...opts, onSuccess: callback || noop },
  );
};
